import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import LogInlineLink from '#component/general/log-inline-link';
import { projectEventType } from '#type';
import { getDeviceUrl } from '#helper';

const EventDescription = ({ log }) => {
  const {
    project,
    user,
    device,
    alert,
    product,
    packageType,
    event: { id },
    data,
    module,
    timestamp
  } = log;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    CREATE_PROJECT,
    EDIT_PROJECT,
    DELETE_PROJECT,
    ADD_DEVICE,
    DELETE_DEVICE,
    ADD_USER,
    DELETE_USER,
    ADD_ALERT,
    EDIT_ALERT,
    DELETE_ALERT,
    ADD_PRODUCT,
    EDIT_PRODUCT,
    DELETE_PRODUCT,
    ADD_PACKAGE_TYPE,
    EDIT_PACKAGE_TYPE,
    DELETE_PACKAGE_TYPE,
    ASSIGN_MODULE_TO_PROJECT,
    UNASSIGN_MODULE_FROM_PROJECT,
    ADD_CUSTOM_FIELD,
    EDIT_CUSTOM_FIELD,
    DELETE_CUSTOM_FIELD,
    CREATE_ALERT_CONFIRMATION,
    EDIT_ALERT_CONFIRMATION,
    DELETE_ALERT_CONFIRMATION
  } = projectEventType;

  try {
    const userFirstAndLastName = (
      <LogInlineLink to={`/user/${user?.id}`} text={`${user?.firstName} ${user?.lastName}`} isMobile={isMobile} />
    );

    const projectLabel = (
      <LogInlineLink to={`/project/edit/${project.id}`} text={`"${project.title}"`} isMobile={isMobile} />
    );

    const deviceTitle = `${device?.title || device?.uid}`;
    const deviceUrl = getDeviceUrl(device?.id, timestamp);
    const deviceLabel = (
      <LogInlineLink to={deviceUrl} text={deviceTitle} isMobile={isMobile} />
    );

    const alertLabel = `"${alert?.label}"`;
    const productLabel = `"${product?.label}"`;
    const packageTypeLabel = `"${packageType?.label}"`;
    const customFieldLabel = `"${data?.label}"`;
    const moduleLabel = `"${module?.label}"`;
    const alertConfirmationLabel = `"${data?.value}"`;

    switch (id) {
      case CREATE_PROJECT:
        return (
          <>
            Project
            {' '}
            {projectLabel}
            {' '}
            was created
          </>
        );
      case EDIT_PROJECT:
        return (
          <>
            Project
            {' '}
            {projectLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_PROJECT:
        return (
          <>
            Project
            {' '}
            {projectLabel}
            {' '}
            was removed
          </>
        );
      case ADD_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case DELETE_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case ADD_USER:
        return (
          <>
            {userFirstAndLastName}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case DELETE_USER:
        return (
          <>
            {userFirstAndLastName}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case ADD_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case EDIT_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case ADD_PRODUCT:
        return (
          <>
            Product
            {' '}
            {productLabel}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case EDIT_PRODUCT:
        return (
          <>
            Product
            {' '}
            {productLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_PRODUCT:
        return (
          <>
            Product
            {' '}
            {productLabel}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case ADD_PACKAGE_TYPE:
        return (
          <>
            Package type
            {' '}
            {packageTypeLabel}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case EDIT_PACKAGE_TYPE:
        return (
          <>
            Package type
            {' '}
            {packageTypeLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_PACKAGE_TYPE:
        return (
          <>
            Package type
            {' '}
            {packageTypeLabel}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case ASSIGN_MODULE_TO_PROJECT:
        return (
          <>
            Module
            {' '}
            {moduleLabel}
            {' '}
            was assigned to project
            {' '}
            {projectLabel}
          </>
        );
      case UNASSIGN_MODULE_FROM_PROJECT:
        return (
          <>
            Module
            {' '}
            {moduleLabel}
            {' '}
            was unassign
          </>
        );
      case ADD_CUSTOM_FIELD:
        return (
          <>
            Custom field
            {' '}
            {customFieldLabel}
            {' '}
            was added to project
            {' '}
            {projectLabel}
          </>
        );
      case EDIT_CUSTOM_FIELD:
        return (
          <>
            Custom field
            {' '}
            {customFieldLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_CUSTOM_FIELD:
        return (
          <>
            Custom field
            {' '}
            {customFieldLabel}
            {' '}
            was removed from project
            {' '}
            {projectLabel}
          </>
        );
      case CREATE_ALERT_CONFIRMATION:
        return (
          <>
            Alert confirmation
            {' '}
            {alertConfirmationLabel}
            {' '}
            was created in project
            {' '}
            {projectLabel}
          </>
        );
      case EDIT_ALERT_CONFIRMATION:
        return (
          <>
            Alert confirmation
            {' '}
            {alertConfirmationLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_ALERT_CONFIRMATION:
        return (
          <>
            Alert confirmation
            {' '}
            {alertConfirmationLabel}
            {' '}
            was deleted from project
            {' '}
            {projectLabel}
          </>
        );
      default:
        return 'Not recognized event';
    }
  } catch (e) {
    return 'Not recognized event';
  }
};

export default EventDescription;
